import { useState } from 'react';
import { motion } from 'motion/react';
import { Card } from './ui/card';
import { ExternalLink, Phone, Mail, Clock, MapPin, Navigation, Globe } from 'lucide-react';
import { Button } from './ui/button';

interface Location {
  id: string;
  name: string;
  address: string;
  phone?: string;      // optional: hide when missing
  email?: string;      // optional: hide when missing
  country: string;
  timezone: string;
  workingHours?: string;
  mapLink: string;
  embedUrl?: string;   // optional: prefer when present
}

export function GoogleMap() {
  // Default view
  const [selectedId, setSelectedId] = useState<string>('oman');

  // Keep order: Oman, India, Dubai
  const locations: Location[] = [
    {
      id: 'oman',
      name: 'amasQIS.ai OMAN',
      address: 'Muscat Governorate - Al Mawaleh, P.O. Box: 2223, Postal Code: 123',
      phone: '+968 7926 2675',
      email: 'info@amasqis.ai',
      country: 'Oman',
      timezone: 'Gulf Standard Time (GST) ',
      workingHours: '8:00 AM - 6:00 PM',
      mapLink: 'https://maps.app.goo.gl/6Qg47TEsh2NDQtP17',
      embedUrl:
        'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d233668.3826906843!2d58.14157!3d23.5880!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e91ffa7c8a9b2e1%3A0x9eb7d8b9d8b9d8b9!2sAl%20Mawaleh%2C%20Oman!5e0!3m2!1sen!2s!4v1639633200000!5m2!1sen!2s',
    },
    {
      id: 'india',
      name: 'amasQIS.ai INDIA',
      address: 'Sasurie College Campus, Coimbatore Bypass (NH544) Vijayamangalam, Tirupur Dt, Tamil Nadu – 638056.',
      phone: '+91 90252 26518',
      email: 'info@amasqis.ai',
      country: 'India',
      timezone: 'Indian Standard Time (IST)',
      workingHours: '9:00 AM - 7:00 PM',
      mapLink: 'https://maps.app.goo.gl/9zNtmxqyxRmEbjVp9',
      embedUrl:
        'https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d7827.2170562244755!2d77.4893603!3d11.2165729!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba90dcbca0e47c7%3A0x1efd327382d41315!2sSASURIE%20COLLEGE%20OF%20EDUCATION!5e0!3m2!1sen!2sin!4v1769586582471!5m2!1sen!2sin" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade',
    },
    {
      id: 'dubai',
      name: 'amasQIS.ai DUBAI',
      address: 'AL SHARIF BUILDING 2,2. post Box :88878, Dubai',
      phone: '+91 77081 13987',
      email: 'info@amasqis.ai',
      country: 'United Arab Emirates',
      timezone: 'Gulf Standard Time (GST)',
      workingHours: '9:00 AM - 6:00 PM',
      mapLink:
        'https://www.google.com/maps/search/?api=1&query=AL%20SHARIF%20BUILDING%202%2C2.%20post%20Box%20%3A88878%2C%20Dubai',
      // Embedded map URL (uses the same place search as iframe fallback)
      embedUrl:
        'https://www.google.com/maps/embed/v1/place?key=AIzaSyDZf27vf5BxGG7-HMrRKw8bh67oFmDzkTU&q=AL%20SHARIF%20BUILDING%202%2C2.%20post%20Box%20%3A88878%2C%20Dubai&zoom=15&maptype=roadmap',
    },
    {
      id: 'uk',
      name: 'amasQIS.ai Ltd UNITED KINGDOM',
      address: '128 CITY ROAD, LONDON, UNITED KINGDOM, EC1V 2NX',
      email: 'info@amasqis.ai',
      country: 'United Kingdom',
      timezone: 'Greenwich Mean Time (GMT)',
      workingHours: '9:00 AM - 6:00 PM',
      mapLink:
        'https://www.google.com/maps/search/?api=1&query=128%20CITY%20ROAD%2C%20LONDON%2C%20EC1V%202NX%2C%20UNITED%20KINGDOM',
      embedUrl:
        'https://www.google.com/maps/embed/v1/place?key=AIzaSyDZf27vf5BxGG7-HMrRKw8bh67oFmDzkTU&q=128%20CITY%20ROAD%2C%20LONDON%2C%20EC1V%202NX%2C%20UNITED%20KINGDOM&zoom=15&maptype=roadmap',
    }

  ];

  const currentLocation = locations.find((loc) => loc.id === selectedId);

  const isPrimaryTheme = (locId: string) => locId === 'oman'; // Oman uses "primary", others "accent"
  const labelFor = (locId: string) =>
    locId === 'oman'
      ? '🇴🇲 Oman Office'
      : locId === 'india'
        ? '🇮🇳 India Office'
        : locId === 'dubai'
          ? '🇦🇪 Dubai Office'
          : '🇬🇧 UK Office';

  return (
    <div className="w-full">
      {/* Full Width Container */}
      <div className="w-full max-w-none">
        {/* Header Section - Title */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-4 mb-4"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-accent/20 rounded-xl flex items-center justify-center">
              <Globe className="w-6 h-6 text-primary" />
            </div>
            <div className="text-left">
              <h2 className="text-3xl font-bold">Our Global Offices</h2>
              <p className="text-muted-foreground">
                Connect with us worldwide • Professional support in multiple regions
              </p>
            </div>
          </motion.div>

          {/* Toggle Switch - Below Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex justify-center"
          >
            <div className="flex items-center gap-1 bg-black/20 backdrop-blur-sm rounded-xl p-1 border border-white/10">
              {locations.map((location) => {
                const selected = selectedId === location.id;
                const activeClass = isPrimaryTheme(location.id)
                  ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30'
                  : 'bg-accent text-accent-foreground shadow-lg shadow-accent/30';
                return (
                  <button
                    key={location.id}
                    onClick={() => setSelectedId(location.id)}
                    className={`
                      flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-300 font-medium
                      ${selected ? `${activeClass} neon-glow` : 'text-muted-foreground hover:text-foreground hover:bg-white/5'}
                    `}
                  >
                    <div className="w-3 h-3 rounded-full bg-current" />
                    <span className="font-semibold">{labelFor(location.id)}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          {/* Left Column - Office Details */}
          <motion.div
            key={currentLocation?.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col"
          >
            {currentLocation && (
              <Card className="glass-strong p-8 flex-1 flex flex-col">
                {/* Office Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center ${isPrimaryTheme(currentLocation.id)
                        ? 'bg-primary/20 text-primary'
                        : 'bg-accent/20 text-accent'
                      }`}
                  >
                    <MapPin className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">{currentLocation.name}</h3>
                    <p className="text-muted-foreground text-lg">
                      {currentLocation.country} • {currentLocation.timezone}
                    </p>
                  </div>
                </div>

                {/* Contact Details */}
                <div className="space-y-6 flex-1">
                  {/* Address */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-foreground mb-1">Office Address</p>
                      <p className="text-muted-foreground leading-relaxed">{currentLocation.address}</p>
                    </div>
                  </div>

                  {/* Phone (hidden if missing) */}
                  {currentLocation.phone && (
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                        <Phone className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-foreground mb-1">Phone Number</p>
                        <a
                          href={`tel:${currentLocation.phone}`}
                          className="text-primary hover:text-accent transition-colors font-medium"
                        >
                          {currentLocation.phone}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Email (hidden if missing) */}
                  {currentLocation.email && (
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                        <Mail className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-foreground mb-1">Email Address</p>
                        <a
                          href={`mailto:${currentLocation.email}`}
                          className="text-primary hover:text-accent transition-colors font-medium"
                        >
                          {currentLocation.email}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Working Hours */}
                  {currentLocation.workingHours && (
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-accent/20 rounded-lg flex items-center justify-center">
                        <Clock className="w-5 h-5 text-accent" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-foreground mb-1">Business Hours</p>
                        <p className="text-accent font-medium">{currentLocation.workingHours}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-3 mt-8 pt-6 border-t border-white/10">
                  <Button
                    onClick={() => window.open(currentLocation.mapLink, '_blank')}
                    className={`w-full ${isPrimaryTheme(currentLocation.id)
                        ? 'bg-primary hover:bg-primary/80 text-primary-foreground'
                        : 'bg-accent hover:bg-accent/80 text-accent-foreground'
                      } neon-glow`}
                    size="lg"
                  >
                    <Navigation className="w-5 h-5 mr-2" />
                    Open in Google Maps
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() =>
                      window.open(
                        `https://maps.google.com/?q=${encodeURIComponent(currentLocation.address)}`,
                        '_blank'
                      )
                    }
                    className="w-full glass border-primary/30 hover:border-primary/50"
                    size="lg"
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Get Directions
                  </Button>
                </div>
              </Card>
            )}
          </motion.div>

          {/* Right Column - Map */}
          <motion.div
            key={`${currentLocation?.id}-map`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex flex-col"
          >
            {currentLocation && (
              <Card className="glass overflow-hidden border-primary/20 flex-1 flex flex-col">
                {/* Map Header */}
                <div className="p-4 border-b border-white/10 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-3 h-3 rounded-full ${isPrimaryTheme(currentLocation.id) ? 'bg-primary' : 'bg-accent'
                          }`}
                      />
                      <span className="font-semibold">{currentLocation.name}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(currentLocation.mapLink, '_blank')}
                      className="glass-strong border-white/20 hover:border-primary/50"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                {/* Embedded Map */}
                <div className="relative flex-1 min-h-[400px]">
                  <iframe
                    src={
                      currentLocation.embedUrl
                        ? currentLocation.embedUrl
                        : `https://www.google.com/maps/embed/v1/place?key=AIzaSyDZf27vf5BxGG7-HMrRKw8bh67oFmDzkTU&q=${encodeURIComponent(
                          currentLocation.address
                        )}&zoom=15&maptype=roadmap`
                    }
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`${currentLocation.name} Location Map`}
                    className="w-full h-full absolute inset-0"
                  />
                </div>
              </Card>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}